import { lazy, Suspense, useCallback, useState } from 'react';
import { Icon } from '../components/Icon/Icon';
import { useT } from '../i18n';
import { shortCode, type Locale } from '../i18n/locales/registry';
import { useLanguage } from '../i18n/locales/state';

const loadSheet = () => import('../components/LanguageSheet/LanguageSheet');
const LanguageSheet = lazy(loadSheet);

/** Its own name in its own language, where the browser knows it ('Deutsch'), else the code. */
function ownName(l: Locale): string {
  try {
    return new Intl.DisplayNames([l], { type: 'language' }).of(l) ?? l;
  } catch {
    return l;
  }
}

/** The header's globe and code, on every screen; opens the language sheet. */
export function LanguageButton() {
  const t = useT();
  const { active } = useLanguage();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type="button"
        className="langbtn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('common.lang.button', { language: ownName(active) })}
        onPointerEnter={() => void loadSheet()}
        onFocus={() => void loadSheet()}
        onClick={() => setOpen(true)}
        data-testid="chrome-lang"
      >
        <Icon name="globe" size={20} />
        <span>{shortCode(active)}</span>
      </button>
      {open && (
        <Suspense fallback={null}>
          <LanguageSheet open onClose={close} onPicked={close} />
        </Suspense>
      )}
    </>
  );
}
